/**
 * Campaign Results — analytics for a single campaign.
 *
 * Data source: GET /admin/analytics/campaigns/:id (plays, players, rewards,
 * attributed orders from order webhooks, outcomes, timeline).
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  InlineGrid,
  Text,
  Banner,
  Spinner,
  DataTable,
  Divider,
  Box,
} from "@shopify/polaris";
import { getCampaignAnalytics, type CampaignMetrics } from "../api/analytics";
import { isShopInstallRedirectError } from "../api/admin-api-client";

function humanizeOutcomeKey(key: string): string {
  const spaced = key.replace(/[_-]+/g, " ").trim();
  if (!spaced) return key;
  return spaced
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function outcomeDisplayName(o: CampaignMetrics["outcomes"][0]): string {
  if (o.label && o.label !== o.outcomeKey) return o.label;
  return humanizeOutcomeKey(o.outcomeKey);
}

function formatPeriodHeading(period: string): string {
  const d = new Date(`${period}T12:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return period;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatMoneyLine(subtotal: string, currency: string | null): string {
  const n = Number.parseFloat(subtotal);
  if (Number.isNaN(n)) return subtotal;
  if (currency && /^[A-Z]{3}$/i.test(currency)) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency.toUpperCase(),
      }).format(n);
    } catch {
      /* fall through */
    }
  }
  return `${subtotal}${currency ? ` ${currency}` : ""}`;
}

function CampaignAnalyticsPage() {
  const { id: campaignId } = useParams<{ id: string }>();
  const [metrics, setMetrics] = useState<CampaignMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    if (!campaignId) {
      setError("Campaign ID is missing.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getCampaignAnalytics(campaignId);
      setMetrics(data);
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      console.error("Failed to fetch campaign analytics:", err);
      setError(err instanceof Error ? err.message : "Failed to load campaign analytics.");
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const timelineNewestFirst = useMemo(() => {
    if (!metrics?.timeline.length) return [];
    return [...metrics.timeline].sort((a, b) => b.period.localeCompare(a.period));
  }, [metrics?.timeline]);

  const outcomeTableRows = useMemo(() => {
    if (!metrics?.outcomes.length) return [];
    return metrics.outcomes.map((o) => [
      outcomeDisplayName(o),
      o.count.toLocaleString(),
      `${o.percentage.toFixed(1)}%`,
    ]);
  }, [metrics?.outcomes]);

  const timelineTableRows = useMemo(() => {
    return timelineNewestFirst.map((row) => [
      formatPeriodHeading(row.period),
      row.plays.toLocaleString(),
      row.rewards.toLocaleString(),
      row.uniquePlayers.toLocaleString(),
    ]);
  }, [timelineNewestFirst]);

  if (loading) {
    return (
      <Page title="Campaign results">
        <Card>
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <Spinner size="large" />
          </div>
        </Card>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Campaign results">
        <Banner tone="critical">{error}</Banner>
      </Page>
    );
  }

  if (!metrics) {
    return (
      <Page title="Campaign results">
        <Banner tone="info">No analytics data available for this campaign.</Banner>
      </Page>
    );
  }

  return (
    <Page
      title="Results"
      subtitle={metrics.campaignName}
      backAction={{ content: "Campaigns", url: "/campaigns" }}
      secondaryActions={[
        {
          content: "Edit campaign",
          url: `/campaigns/${campaignId}`,
        },
      ]}
    >
      <BlockStack gap="500">
        <Box paddingBlockEnd="100">
          <Text as="p" variant="bodyMd" tone="subdued">
            Plays, rewards, and spin outcomes from sessions. Attributed orders and attributed
            sales apply only to discount codes issued via reward claim (not wheel-only static
            codes). Timeline covers the last 30 days.
          </Text>
        </Box>

        {/* Primary metrics: engagement first, then reward outcomes */}
        <Card>
          <BlockStack gap="500">
            <BlockStack gap="100">
              <Text as="h2" variant="headingSm" tone="subdued">
                At a glance
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Start with volume (plays) and reach (unique players), then reward results.
              </Text>
            </BlockStack>

            <InlineGrid columns={{ xs: 1, sm: 2 }} gap="400">
              <Box padding="400" background="bg-surface-secondary" borderRadius="200">
                <BlockStack gap="200">
                  <Text as="p" variant="bodySm" tone="subdued">
                    Total plays
                  </Text>
                  <Text as="p" variant="heading3xl" fontWeight="bold">
                    {metrics.totalPlays.toLocaleString()}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Every completed play session
                  </Text>
                </BlockStack>
              </Box>
              <Box padding="400" background="bg-surface-secondary" borderRadius="200">
                <BlockStack gap="200">
                  <Text as="p" variant="bodySm" tone="subdued">
                    Unique players
                  </Text>
                  <Text as="p" variant="heading2xl" fontWeight="bold">
                    {metrics.uniquePlayers.toLocaleString()}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Distinct players in this campaign
                  </Text>
                </BlockStack>
              </Box>
            </InlineGrid>

            <Divider />

            <InlineStack gap="600" wrap blockAlign="start">
              <BlockStack gap="150">
                <Text as="p" variant="bodySm" tone="subdued">
                  Rewards granted
                </Text>
                <Text as="p" variant="headingLg" fontWeight="semibold">
                  {metrics.rewardsGranted.toLocaleString()}
                </Text>
              </BlockStack>
              <BlockStack gap="150">
                <Text as="p" variant="bodySm" tone="subdued">
                  Rewards rejected
                </Text>
                <Text as="p" variant="headingLg" fontWeight="semibold">
                  {metrics.rewardsRejected.toLocaleString()}
                </Text>
              </BlockStack>
              <BlockStack gap="150">
                <Text as="p" variant="bodySm" tone="subdued">
                  Attributed orders
                </Text>
                <Text as="p" variant="headingLg" fontWeight="semibold">
                  {metrics.attributedOrders.toLocaleString()}
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Distinct paid orders where our issued discount code was applied at checkout
                </Text>
              </BlockStack>
            </InlineStack>

            <Divider />

            <BlockStack gap="300">
              <Text as="h3" variant="headingSm">
                Attributed sales
              </Text>
              {metrics.attributedSalesMixedCurrency &&
              metrics.attributedSalesByCurrency &&
              metrics.attributedSalesByCurrency.length > 0 ? (
                <BlockStack gap="300">
                  <Text as="p" variant="bodySm" tone="subdued">
                    Matched orders use more than one currency — subtotals are shown per currency
                    (not added together).
                  </Text>
                  <DataTable
                    columnContentTypes={["text", "numeric"]}
                    headings={["Currency", "Order subtotals (sum)"]}
                    rows={metrics.attributedSalesByCurrency.map((row) => [
                      row.currency,
                      formatMoneyLine(
                        row.subtotal,
                        /^[A-Za-z]{3}$/.test(row.currency) && row.currency !== "unknown"
                          ? row.currency
                          : null
                      ),
                    ])}
                    increasedTableDensity
                  />
                  <Text as="p" variant="bodySm" tone="subdued">
                    Sum of Shopify order subtotal for each redemption row matched to this campaign
                    (RewardGrant-issued codes via orders/paid). Not net revenue or profit.
                  </Text>
                </BlockStack>
              ) : (
                <BlockStack gap="150">
                  <Text as="p" variant="headingLg" fontWeight="semibold">
                    {metrics.attributedRevenueSubtotal !== null
                      ? formatMoneyLine(
                          metrics.attributedRevenueSubtotal,
                          metrics.attributedSalesCurrency
                        )
                      : "—"}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Sum of Shopify order subtotals for those attributed orders (same currency).
                    Based on checkout subtotal when the code was applied — not net profit, not
                    total store revenue, and only for codes we issued through this campaign’s
                    rewards.
                  </Text>
                </BlockStack>
              )}
            </BlockStack>
          </BlockStack>
        </Card>

        {/* Outcomes */}
        <Card>
          <BlockStack gap="400">
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd">
                What players landed on
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Share of plays by outcome. Rows are ordered by how often each outcome occurred.
              </Text>
            </BlockStack>
            {metrics.outcomes.length > 0 ? (
              <DataTable
                columnContentTypes={["text", "numeric", "numeric"]}
                headings={["Outcome", "Plays", "Share of plays"]}
                rows={outcomeTableRows}
                increasedTableDensity
              />
            ) : (
              <Text as="p" tone="subdued">
                No outcome breakdown yet — outcomes appear after plays record an outcome.
              </Text>
            )}
          </BlockStack>
        </Card>

        {/* Timeline */}
        <Card>
          <BlockStack gap="400">
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd">
                Daily activity (last 30 days)
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Newest days first. Same calendar-day buckets as the backend (UTC dates).
              </Text>
            </BlockStack>
            {timelineNewestFirst.length > 0 ? (
              <DataTable
                columnContentTypes={["text", "numeric", "numeric", "numeric"]}
                headings={["Day", "Plays", "Rewards", "Unique players"]}
                rows={timelineTableRows}
                increasedTableDensity
              />
            ) : (
              <Text as="p" tone="subdued">
                No session data in the last 30 days.
              </Text>
            )}
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}

export default CampaignAnalyticsPage;
